/**
 * Club Validation Component
 * 
 * Handles club ID validation, UI state management, and checkout redirection
 */

import { Component } from '@theme/component';
import { validateClubId } from '@form/club-service';
import {
  showLoadingSpinner,
  hideLoadingSpinner,
  showSuccessState,
  showErrorState,
  transformButtonToValidate,
  validateClubIdFormat
} from '@form/utils';
import { CSS_CLASSES } from '@form/constants';
import { EVENTS, STORAGE_KEYS } from '@form/data-constants';
import { updateOrderAttributes } from '@theme/order-attributes';
import {
  removeAllDiscounts,
  applyDiscountCode,
  clearClubAppliedFlag,
  formatDiscountMessage,
  getTranslation,
  toggleCheckoutButtons,
  disableCheckoutButtons,
  enableCheckoutButtons,
} from './club-validation.utils.js';
import { CLUB_VALIDATION_SELECTORS, CLUB_VALIDATION_DEFAULTS } from './club-validation.constants.js';

/**
 * @typedef {Object} ClubValidationRefs
 * @property {HTMLInputElement} clubIdInput - Club ID input field
 * @property {HTMLButtonElement} validateBtn - Validate button
 * @property {HTMLElement} validateBtnText - Validate button text element
 * @property {HTMLElement} validateBtnSpinner - Validate button spinner element
 * @property {HTMLElement} successMessage - Success message container
 * @property {HTMLElement} errorMessage - Error message container
 */

/**
 * Club validation web component
 * @extends {Component<ClubValidationRefs>}
 */
class ClubValidationComponent extends Component {
  /**
   * Handles custom checkout button clicks
   * @type {(event: MouseEvent) => void}
   */
  #handleCustomCheckoutClick = (event) => {
    if (!this.#state.redirectUrl) {
      return;
    }

    event.preventDefault();
    window.location.href = this.#state.redirectUrl;
  };

  /**
   * Required refs for this component
   * @type {string[]}
   */
  requiredRefs = ['validateBtn', 'validateBtnText', 'validateBtnSpinner'];

  /**
   * Component state
   * @type {{isValidated: boolean, clubData: Object|null, redirectUrl: string|null}}
   */
  #state = {
    isValidated: false,
    clubData: null,
    redirectUrl: null,
  };

  /**
   * Private element references (not managed by Component base class)
   * @type {{
   *  clubIdInput: HTMLInputElement|null,
   *  successMessage: HTMLElement|null,
   *  errorMessage: HTMLElement|null,
   *  cartItemsComponent: HTMLElement|null,
   *  ctasContainer: HTMLElement|null,
   *  defaultCheckoutButton: HTMLButtonElement|null,
   *  customCheckoutButton: HTMLButtonElement|null
   * }}
   */
  #elements = {
    clubIdInput: null,
    successMessage: null,
    errorMessage: null,
    extraMessage: null,
    cartItemsComponent: null,
    ctasContainer: null,
    defaultCheckoutButton: null,
    customCheckoutButton: null,
  };

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  /**
   * Initialize component
   */
  #initialize() {
    // Find additional elements not in requiredRefs
    const clubIdInput = this.querySelector(CLUB_VALIDATION_SELECTORS.clubIdInput);
    const successMessage = this.querySelector(CLUB_VALIDATION_SELECTORS.successMessage);
    const errorMessage = this.querySelector(CLUB_VALIDATION_SELECTORS.errorMessage);

    // Store element references as private properties
    if (clubIdInput instanceof HTMLInputElement) {
      this.#elements.clubIdInput = clubIdInput;
    } else {
      console.warn('Club ID input not found');
    }

    if (successMessage instanceof HTMLElement) {
      this.#elements.successMessage = successMessage;
    } else {
      console.warn('Success message element not found');
    }

    if (errorMessage instanceof HTMLElement) {
      this.#elements.errorMessage = errorMessage;
    } else {
      console.warn('Error message element not found');
    }

    const extraMessage = this.querySelector(CLUB_VALIDATION_SELECTORS.extraMessage);
    if (extraMessage instanceof HTMLElement) {
      this.#elements.extraMessage = extraMessage;
    }

    const cartItemsComponent = this.closest(CLUB_VALIDATION_SELECTORS.cartItemsComponent);

    if (cartItemsComponent instanceof HTMLElement) {
      this.#elements.cartItemsComponent = cartItemsComponent;

      const ctasContainer = cartItemsComponent.querySelector(CLUB_VALIDATION_SELECTORS.ctasContainer);

      if (ctasContainer instanceof HTMLElement) {
        this.#elements.ctasContainer = ctasContainer;

        const defaultCheckoutButton = ctasContainer.querySelector(
          CLUB_VALIDATION_SELECTORS.defaultCheckoutButton
        );

        if (defaultCheckoutButton instanceof HTMLButtonElement) {
          this.#elements.defaultCheckoutButton = defaultCheckoutButton;
        }

        const existingCustomButton = ctasContainer.querySelector(CLUB_VALIDATION_SELECTORS.customCheckoutButton);

        if (existingCustomButton instanceof HTMLButtonElement) {
          this.#elements.customCheckoutButton = existingCustomButton;
          existingCustomButton.addEventListener('click', this.#handleCustomCheckoutClick);
        }
      } else {
        console.warn('Cart CTA container not found');
      }
    }

    // Store redirect URL from data attribute
    this.#state.redirectUrl = this.dataset.redirectUrl || null;
  }

  /**
   * Handles form submission for club ID validation
   * Called via on:submit="/validate" in liquid template
   * @param {Event} event - Form submit event
   */
  async validate(event) {
    event.preventDefault();

    const clubId = this.#elements.clubIdInput?.value?.trim();

    if (!clubId) return;

    if (!validateClubIdFormat(clubId)) {
      this.#showError();
      return;
    }

    this.#showLoading();

    try {
      const response = await validateClubId(clubId);

      if (response.success && response.data) {
        this.#handleValidationSuccess(response.data);
      } else {
        this.#showError();
      }
    } catch (error) {
      console.error('Validation error:', error);
      this.#showError();
    } finally {
      this.#hideLoading();
    }
  }

  /**
   * Handles successful validation
   * @param {Object} clubData - Club data from API
   */
  async #handleValidationSuccess(clubData) {
    this.#state.isValidated = true;
    this.#state.clubData = clubData;

    showSuccessState(this.#elements.successMessage, this.#elements.errorMessage);

    // Display discount amount if available (localized)
    if (this.#elements.extraMessage) {
      const discountMessage = formatDiscountMessage(clubData.discount_amount);
      if (discountMessage) {
        this.#elements.extraMessage.textContent = discountMessage;
        this.#elements.extraMessage.classList.remove('hidden');
      } else {
        this.#elements.extraMessage.classList.add('hidden');
      }
    }

    const sectionId = this.dataset.sectionId;

    // If pharmacy_pickup_payment is true, remove any existing discount codes
    // to prevent double discounting (discount will be calculated locally)
    if (sectionId && clubData.pharmacy_pickup_payment === true) {
      await removeAllDiscounts(sectionId);
      clearClubAppliedFlag();
    }

    // Store club_id, pharmacy_pickup_payment, and discount_amount_percentage in order attributes for persistence across section re-renders
    if (sectionId && clubData.club_id) {
      await updateOrderAttributes(sectionId, {
        club_id: clubData.club_id,
        pharmacy_pickup_payment: String(clubData.pharmacy_pickup_payment === true),
        discount_amount_percentage: String(clubData.discount_amount || ''),
      });
    }

    // Toggle checkout button visibility based on pharmacy pickup payment
    const showCustomButton = clubData.pharmacy_pickup_payment === true;
    toggleCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton,
      showCustomButton,
      CSS_CLASSES.HIDDEN
    );

    // Apply discount code only if pharmacy_pickup_payment is false
    // If pharmacy_pickup_payment is true, discount will be calculated locally in form cart summary
    if (sectionId && clubData.pharmacy_pickup_payment !== true) {
      const discountComponent = document.querySelector(CLUB_VALIDATION_SELECTORS.discountComponent);
      const discountSectionId = discountComponent?.dataset.sectionId;
      if (discountSectionId) {
        await applyDiscountCode(clubData.club_id, discountSectionId);
      }
    }

    // Dispatch event for form data handler
    const event = new CustomEvent(EVENTS.CLUB_VALIDATED, {
      detail: { clubData },
      bubbles: true,
    });
    document.dispatchEvent(event);

    // Re-enable checkout buttons after successful validation
    enableCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton,
      this.#elements.cartItemsComponent
    );

    this.#dispatchClubAccessUpdate(true);
  }

  /**
   * Shows loading state
   */
  #showLoading() {
    showLoadingSpinner(
      this.refs.validateBtn,
      this.refs.validateBtnText,
      this.refs.validateBtnSpinner
    );
    // Disable checkout buttons during validation
    disableCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton
    );
  }

  /**
   * Hides loading state
   */
  #hideLoading() {
    // Get the original button text from the translate helper or data attribute
    const originalText = this.refs.validateBtnText?.dataset.originalText
      || getTranslation('validate')
      || CLUB_VALIDATION_DEFAULTS.validateButtonText;

    hideLoadingSpinner(
      this.refs.validateBtn,
      this.refs.validateBtnText,
      this.refs.validateBtnSpinner,
      originalText
    );
    // Re-enable checkout buttons after validation completes
    enableCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton,
      this.#elements.cartItemsComponent
    );
  }

  /**
   * Shows error state
   */
  async #showError() {
    showErrorState(this.#elements.successMessage, this.#elements.errorMessage);
    transformButtonToValidate(this.refs.validateBtn);
    this.#state.isValidated = false;
    this.#state.clubData = null;

    // Reset checkout buttons to default state
    toggleCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton,
      false,
      CSS_CLASSES.HIDDEN
    );
    // Re-enable checkout buttons after error
    enableCheckoutButtons(
      this.#elements.defaultCheckoutButton,
      this.#elements.customCheckoutButton,
      this.#elements.cartItemsComponent
    );

    // Hide discount message on error
    if (this.#elements.extraMessage) {
      this.#elements.extraMessage.classList.add('hidden');
      this.#elements.extraMessage.textContent = '';
    }

    // Clear club_id, pharmacy_pickup_payment, and discount_amount_percentage from order attributes and remove any existing discounts
    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      // Clear the club_id, pharmacy_pickup_payment, and discount_amount_percentage from order attributes first (this updates button state immediately)
      await updateOrderAttributes(sectionId, {
        club_id: '',
        pharmacy_pickup_payment: '',
        discount_amount_percentage: '',
      }, [], true
      );

      // Then remove any existing discounts
      await removeAllDiscounts(sectionId);
    }

    // Clear the club applied flag from discount component
    clearClubAppliedFlag();

    // Clear only our session storage keys
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.CLUB_DATA);
      sessionStorage.removeItem(STORAGE_KEYS.PHARMACY_DATA);
    }

    this.#dispatchClubAccessUpdate(false);
  }


  /**
   * Handles removing the club ID
   * Called via on:click="/removeClubId" in liquid template
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event
   */
  async removeClubId(event) {
    event.preventDefault();
    event.stopPropagation();

    // Clear the input field
    if (this.#elements.clubIdInput instanceof HTMLInputElement) {
      this.#elements.clubIdInput.value = '';
    }

    // Trigger error state which will clean up everything
    await this.#showError();
  }

  /**
   * Broadcasts club access changes so other scripts can toggle UI
   * @param {boolean} hasAccess - Whether the user currently has club access
   */
  #dispatchClubAccessUpdate(hasAccess) {
    const accessEvent = new CustomEvent(EVENTS.CLUB_ACCESS_UPDATED, {
      detail: { hasAccess },
      bubbles: true,
    });

    document.dispatchEvent(accessEvent);
  }
}

if (!customElements.get('club-validation-component')) {
  customElements.define('club-validation-component', ClubValidationComponent);
}

